import { Alert, Button, Form, Input, Tooltip, message } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import React, { useEffect, useState } from 'react';
import { resetPassword } from '../../utils/httpRequest';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { selectUserId, selectUsername, setUser } from '../../slices/profile';
import { useTranslation } from 'react-i18next';
import ActionHeader from '../../components/actionHeader';
import Page from '../../components/page';
import { ChangePasswordContainer } from './styles';
import {
  validatePassword,
  getPasswordConditions,
  PASSWORD_CONDITIONS_TEXT,
} from '../../utils/password';
import { useAppDispatch } from '../../store';

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');

  const passwordConditions = getPasswordConditions(currentPassword);
  const userId = useSelector(selectUserId);
  const username = useSelector(selectUsername);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
    }
  }, [navigate]);

  const handleChangePassword = async () => {
    try {
      setLoading(true);
      setError('');
      const {
        currentPassword: oldPassword,
        newPassword,
        confirmPassword,
      } = form.getFieldsValue();

      if (newPassword !== confirmPassword) {
        setError('Passwords do not match!');
        return;
      }

      const res = await resetPassword({
        userID: String(userId),
        username,
        oldPassword,
        password: newPassword,
      });

      dispatch(setUser(res));

      setSuccess(true);
      messageApi.success('Password changed successfully!');

      navigate(-1);
    } catch (err) {
      console.log(err);
      setError(err as string);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      handleChangePassword();
    } catch (err) {
      console.log(err);
    }
  };

  return (
    <Page
      header={
        <ActionHeader
          title={t('Change Password')}
          showBack
          onBack={() => navigate(-1)}
        />
      }
    >
      <ChangePasswordContainer>
        {contextHolder}
        {!loading && error && (
          <Alert
            message={error}
            type="error"
            showIcon
            style={{ width: '100%', marginBottom: '16px' }}
          />
        )}
        {!loading && success && (
          <Alert
            message="Password changed successfully!"
            type="success"
            showIcon
            style={{ width: '100%', marginBottom: '16px' }}
          />
        )}

        <Form
          form={form}
          layout="vertical"
          size="large"
          disabled={loading || success}
          onFinish={handleSubmit}
          style={{ width: '480px', margin: '0 auto' }}
        >
          <Form.Item
            label="Current Password"
            name="currentPassword"
            rules={[
              {
                required: true,
                message: 'Please input current password!',
              },
            ]}
          >
            <Input.Password
              placeholder="Enter current password"
              visibilityToggle={{
                visible: passwordVisible,
                onVisibleChange: setPasswordVisible,
              }}
            />
          </Form.Item>

          <Form.Item
            label={
              <span>
                New Password{' '}
                <Tooltip
                  title={
                    <div>
                      <p style={{ margin: 0 }}>
                        Password must contain at least:
                      </p>
                      <ul
                        style={{
                          margin: '4px 0 0',
                          paddingLeft: 16,
                          listStyle: 'none',
                        }}
                      >
                        {PASSWORD_CONDITIONS_TEXT.map((condition) => (
                          <li
                            key={condition.key}
                            style={{
                              fontWeight: passwordConditions[
                                condition.key as keyof typeof passwordConditions
                              ]
                                ? 600
                                : 400,
                            }}
                          >
                            {condition.label}
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                >
                  <InfoCircleOutlined style={{ color: '#999' }} />
                </Tooltip>
              </span>
            }
            name="newPassword"
            rules={[
              { validator: validatePassword },
              {
                required: true,
                message: 'Please input current password!',
              },
            ]}
          >
            <Input.Password
              placeholder="Enter new password"
              visibilityToggle={{
                visible: passwordVisible,
                onVisibleChange: setPasswordVisible,
              }}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Confirm Password"
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: 'Please confirm password!',
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match!'));
                },
              }),
            ]}
          >
            <Input.Password
              placeholder="Confirm new password"
              visibilityToggle={{
                visible: confirmPasswordVisible,
                onVisibleChange: setConfirmPasswordVisible,
              }}
            />
          </Form.Item>

          <Button
            type="primary"
            size="large"
            block
            loading={loading}
            htmlType="submit"
          >
            Change Password
          </Button>
        </Form>
      </ChangePasswordContainer>
    </Page>
  );
};

export default ChangePassword;
